import { useEffect, useRef, useState } from "react";
import { Flag, X, Check, Eye } from "lucide-react";
import { authFetch, getToken } from "@/adminAuth";

const API = `/api`;

/**
 * Outil admin de nettoyage des rattachements, visible uniquement si un token
 * admin est présent (localStorage). Permet en un clic de retirer une fiche
 * d'un pôle/thème directement depuis la page Ressources, ou de la marquer
 * « à revoir » pour un tri ultérieur au back-office.
 */
export default function TaxonomyFlag({ item, taxonomy, activePole, activeTheme, onChanged }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(false);
  const [poles, setPoles] = useState(item.poles || []);
  const [themes, setThemes] = useState(item.themes || []);
  const [review, setReview] = useState(!!item.needs_review);
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");
  const ref = useRef(null);

  useEffect(() => setIsAdmin(!!getToken()), []);
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!isAdmin) return null;

  const poleLabel = (k) => taxonomy?.poles?.find((p) => p.key === k)?.label || k;
  const themeLabel = (k) => taxonomy?.themes?.find((t) => t.key === k)?.label || k;

  const save = async (p, t, r, msg) => {
    setError("");
    setSaved("");
    try {
      const resp = await authFetch(`${API}/admin/knowledge/${item.type}/${item.slug}`, {
        method: "PUT",
        body: JSON.stringify({ poles: p, themes: t, needs_review: r }),
      });
      if (!resp.ok) throw new Error();
      setSaved(msg);
      setTimeout(() => setSaved(""), 1800);
      onChanged?.(item, p, t, r);
    } catch {
      setError("Échec — reconnectez-vous au back-office.");
    }
  };

  const removePole = (key) => {
    const p = poles.filter((x) => x !== key);
    setPoles(p);
    save(p, themes, review, `Retirée de « ${poleLabel(key)} »`);
  };
  const removeTheme = (key) => {
    const t = themes.filter((x) => x !== key);
    setThemes(t);
    save(poles, t, review, `Retirée de « ${themeLabel(key)} »`);
  };
  const toggleReview = () => {
    const r = !review;
    setReview(r);
    save(poles, themes, r, r ? "Marquée « à revoir »" : "Marque retirée");
  };

  return (
    <div ref={ref} className="absolute top-3 right-3 z-20" data-testid={`flag-wrap-${item.slug}`}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(!open);
        }}
        className={`p-2 border transition-colors ${
          review
            ? "text-amber-400 border-amber-400/50 bg-amber-400/10"
            : "text-[#626773] border-white/10 hover:text-gold hover:border-gold/50 bg-ink/80"
        }`}
        title="Corriger les rattachements (admin)"
        data-testid={`flag-btn-${item.slug}`}
      >
        <Flag className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-9 w-80 bg-ink border border-gold/30 shadow-2xl p-5 text-left"
          data-testid={`flag-popover-${item.slug}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <p className="font-serif text-sm text-[#FAF8F5] leading-snug pr-4">{item.title}</p>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#626773]">
            {item.type_label} — correction admin
          </p>

          {(activePole || activeTheme) && (
            <div className="mt-4 pb-4 border-b border-white/10">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold/70 mb-2">Filtre actif</p>
              {activePole && poles.includes(activePole) && (
                <button
                  onClick={() => removePole(activePole)}
                  className="text-xs text-red-400/90 hover:text-red-400 underline underline-offset-4"
                  data-testid={`flag-quick-pole-${item.slug}`}
                >
                  Retirer du pôle « {poleLabel(activePole)} »
                </button>
              )}
              {activeTheme && themes.includes(activeTheme) && (
                <button
                  onClick={() => removeTheme(activeTheme)}
                  className="block mt-1.5 text-xs text-red-400/90 hover:text-red-400 underline underline-offset-4"
                  data-testid={`flag-quick-theme-${item.slug}`}
                >
                  Retirer du thème « {themeLabel(activeTheme)} »
                </button>
              )}
              {((activePole && !poles.includes(activePole)) || (activeTheme && !themes.includes(activeTheme))) && (
                <p className="text-xs text-[#626773] italic">Déjà hors de ce filtre.</p>
              )}
            </div>
          )}

          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold/70 mb-2">Pôles</p>
            <div className="flex flex-wrap gap-1.5">
              {poles.length === 0 && <span className="text-xs text-[#626773] italic">aucun</span>}
              {poles.map((k) => (
                <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-[#FAF8F5] border border-white/15 px-2 py-1">
                  {poleLabel(k)}
                  <button onClick={() => removePole(k)} aria-label="retirer" data-testid={`flag-remove-pole-${k}-${item.slug}`}>
                    <X className="w-3 h-3 text-red-400/70 hover:text-red-400" />
                  </button>
                </span>
              ))}
            </div>
            <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-gold/70 mb-2">Thèmes</p>
            <div className="flex flex-wrap gap-1.5">
              {themes.length === 0 && <span className="text-xs text-[#626773] italic">aucun</span>}
              {themes.map((k) => (
                <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-[#FAF8F5] border border-white/15 px-2 py-1">
                  {themeLabel(k)}
                  <button onClick={() => removeTheme(k)} aria-label="retirer" data-testid={`flag-remove-theme-${k}-${item.slug}`}>
                    <X className="w-3 h-3 text-red-400/70 hover:text-red-400" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <button
            onClick={toggleReview}
            className={`mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] transition-colors ${
              review ? "text-amber-400" : "text-[#A0A5B1] hover:text-gold"
            }`}
            data-testid={`flag-review-${item.slug}`}
          >
            <Eye className="w-3.5 h-3.5" />
            {review ? "Marquée « à revoir »" : "Marquer « à revoir »"}
          </button>

          {saved && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-400" data-testid={`flag-saved-${item.slug}`}>
              <Check className="w-3.5 h-3.5" /> {saved}
            </p>
          )}
          {error && <p className="mt-3 text-xs text-red-400" data-testid={`flag-error-${item.slug}`}>{error}</p>}
          <p className="mt-3 text-[10px] text-[#626773] leading-relaxed">
            Correction définitive : les règles automatiques ne réattribueront plus cette fiche.
          </p>
        </div>
      )}
    </div>
  );
}
